<template>
  <transition name="dc-modal">
  <div
    v-if="visible"
    class="fixed inset-0 bg-gray-600/50 flex items-center justify-center z-50"
    @click="$emit('close')"
  >
    <div class="dc-modal-panel bg-white rounded-lg shadow-xl w-full max-w-2xl" @click.stop>
      <!-- Header -->
      <div class="flex items-start justify-between px-6 pt-6 pb-4 border-b border-gray-200">
        <div>
          <h3 class="text-lg font-medium text-gray-900">Add Data Connection</h3>
          <p class="mt-1 text-xs text-gray-400">
            Connect Workflow Builder to a data warehouse so its data attributes can
            read from — and write to — your own database.
          </p>
        </div>
        <button class="btn-icon" title="Close" @click="$emit('close')">
          <font-awesome-icon :icon="['fas', 'times']" />
        </button>
      </div>

      <!-- Connection-type tabs -->
      <div class="flex gap-1 px-6 pt-4">
        <button
          v-for="t in types"
          :key="t.key"
          class="px-3 py-2 text-sm font-medium rounded-t-md border-b-2 transition-colors"
          :class="activeType === t.key
            ? 'border-primary-500 text-primary-600'
            : 'border-transparent text-gray-500 hover:text-gray-700'"
          @click="activeType = t.key"
        >
          {{ t.label }}
          <span v-if="t.comingSoon" class="ml-1 text-[10px] uppercase tracking-wide text-gray-400">soon</span>
        </button>
      </div>

      <div class="px-6 py-5 border-t border-gray-200">
        <!-- ── Snowflake (service-account key-pair) ─────────────────────── -->
        <div v-if="activeType === 'snowflake'" class="space-y-4">
          <p class="text-xs text-gray-500">
            Connect to your Snowflake account with a service-account key-pair.
            Credentials are encrypted at rest and used only to run this
            connection's queries.
          </p>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="dc-label">Connection name</label>
              <input v-model.trim="sf.name" type="text" placeholder="e.g. Acme Snowflake" class="dc-input" />
            </div>
            <div>
              <label class="dc-label">Account</label>
              <input v-model.trim="sf.account" type="text" placeholder="xy12345.us-east-1" class="dc-input" />
            </div>
            <div>
              <label class="dc-label">Warehouse</label>
              <input v-model.trim="sf.warehouse" type="text" placeholder="COMPUTE_WH" class="dc-input" />
            </div>
            <div>
              <label class="dc-label">Database</label>
              <input v-model.trim="sf.database" type="text" placeholder="ANALYTICS" class="dc-input" />
            </div>
            <div>
              <label class="dc-label">Role</label>
              <input v-model.trim="sf.role" type="text" placeholder="KAIRON_READER" class="dc-input" />
            </div>
            <div>
              <label class="dc-label">Service-account user</label>
              <input v-model.trim="sf.username" type="text" placeholder="KAIRON_SVC" class="dc-input" />
            </div>
          </div>
          <div>
            <label class="dc-label">Private key (PEM)</label>
            <textarea
              v-model="sf.private_key"
              rows="4"
              spellcheck="false"
              placeholder="-----BEGIN PRIVATE KEY-----&#10;…&#10;-----END PRIVATE KEY-----"
              class="dc-input font-mono text-xs"
            ></textarea>
          </div>
        </div>

        <!-- ── Azure (coming soon) ──────────────────────────────────────── -->
        <div v-else-if="activeType === 'azure'">
          <ComingSoon
            title="Azure connection"
            body="Connect to Azure SQL / Synapse. This connection type is being implemented — planned alongside JDBC/ODBC support."
          />
        </div>

        <!-- ── JDBC / ODBC ─────────────────────────────────────────────── -->
        <div v-else-if="activeType === 'jdbc' && odbcEnabled" class="space-y-4">
          <p class="text-xs text-gray-500">
            Connect to your database over ODBC with a read-only user. The
            connection always uses TLS with certificate verification, and the
            password is encrypted at rest.
          </p>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="dc-label">Connection name</label>
              <input v-model.trim="odbc.name" type="text" placeholder="e.g. Acme Postgres" class="dc-input" />
            </div>
            <div>
              <label class="dc-label">Database engine</label>
              <select v-model="odbc.engine" class="dc-input" @change="applyEnginePort">
                <option v-for="e in engines" :key="e.key" :value="e.key">{{ e.label }}</option>
              </select>
            </div>
            <div>
              <label class="dc-label">Host</label>
              <input v-model.trim="odbc.host" type="text" placeholder="db.example.com" class="dc-input" />
            </div>
            <div>
              <label class="dc-label">Port</label>
              <input v-model.number="odbc.port" type="number" min="1" max="65535" class="dc-input" />
            </div>
            <div>
              <label class="dc-label">Database</label>
              <input v-model.trim="odbc.database" type="text" placeholder="analytics" class="dc-input" />
            </div>
            <div>
              <label class="dc-label">Username</label>
              <input v-model.trim="odbc.username" type="text" placeholder="kairon_reader" class="dc-input" />
            </div>
          </div>
          <div>
            <label class="dc-label">Password</label>
            <input v-model="odbc.password" type="password" autocomplete="new-password" class="dc-input" />
          </div>
        </div>
        <div v-else-if="activeType === 'jdbc'">
          <ComingSoon
            title="Generic JDBC / ODBC"
            body="Connect to your own Postgres database over ODBC. This connection type is being rolled out."
          />
        </div>

        <p v-if="error" class="mt-3 text-xs text-red-dark">{{ error }}</p>
        <p v-if="testResult" class="mt-3 text-xs" :class="testResult.ok ? 'text-green-dark' : 'text-red-dark'">
          {{ testResult.ok ? '✓ Connection succeeded.' : ('✕ ' + testResult.message) }}
        </p>
      </div>

      <!-- Footer -->
      <div class="flex justify-end gap-2 px-6 py-4 border-t border-gray-200">
        <button class="btn-secondary" @click="$emit('close')">Cancel</button>
        <template v-if="activeType === 'snowflake' || (activeType === 'jdbc' && odbcEnabled)">
          <button class="btn-secondary" :disabled="!canSave || testing" @click="handleTest">
            {{ testing ? 'Testing…' : 'Test' }}
          </button>
          <button class="btn-accent" :disabled="!canSave || saving" @click="handleSave">
            {{ saving ? 'Saving…' : 'Add connection' }}
          </button>
        </template>
        <button v-else class="btn-accent" disabled>Add connection</button>
      </div>
    </div>
  </div>
  </transition>
</template>

<script>
import axios from 'axios';

// Azure has no backend yet. JDBC/ODBC opens only when the backend reports it
// enabled (capabilities).
const SOURCE_BASE = '/api/impact_analytics/data-sources/';

const ComingSoon = {
  props: { title: String, body: String },
  template: `
    <div class="flex flex-col items-center gap-2 py-8 text-center">
      <div class="flex h-11 w-11 items-center justify-center rounded-full bg-gray-100 text-gray-400">
        <font-awesome-icon :icon="['fas', 'database']" />
      </div>
      <p class="text-sm font-medium text-gray-700">{{ title }}</p>
      <p class="text-xs text-gray-400 max-w-sm">{{ body }}</p>
      <span class="mt-1 text-[10px] uppercase tracking-wide text-gray-400 border border-gray-200 rounded-full px-2 py-0.5">Coming soon</span>
    </div>`,
};

export default {
  name: 'AddDataConnectionModal',

  components: { ComingSoon },

  props: {
    visible: { type: Boolean, default: false },
  },

  emits: ['close', 'created'],

  data() {
    return {
      activeType: 'snowflake',
      types: [
        { key: 'snowflake', label: 'Snowflake', comingSoon: false },
        { key: 'azure', label: 'Azure', comingSoon: true },
        { key: 'jdbc', label: 'JDBC / ODBC', comingSoon: true },
      ],
      sf: { name: '', account: '', warehouse: '', database: '', role: '', username: '', private_key: '' },
      odbc: { name: '', engine: 'postgresql', host: '', port: 5432, database: '', username: '', password: '' },
      odbcEnabled: false,
      engines: [],
      testing: false,
      saving: false,
      testResult: null,
      error: null,
    };
  },

  computed: {
    canSave() {
      if (this.activeType === 'jdbc') {
        const o = this.odbc;
        return !!(o.name && o.engine && o.host && o.port && o.database && o.username && o.password);
      }
      const s = this.sf;
      return s.name && s.account && s.warehouse && s.database && s.role && s.username && s.private_key;
    },
  },

  watch: {
    visible(open) {
      if (open) this.reset();
    },
  },

  methods: {
    reset() {
      this.activeType = 'snowflake';
      this.sf = { name: '', account: '', warehouse: '', database: '', role: '', username: '', private_key: '' };
      this.odbc = { name: '', engine: 'postgresql', host: '', port: 5432, database: '', username: '', password: '' };
      this.loadCapabilities();
      this.testing = false;
      this.saving = false;
      this.testResult = null;
      this.error = null;
    },

    async loadCapabilities() {
      try {
        const { data } = await axios.get(`${SOURCE_BASE}capabilities/`);
        const odbc = data?.jdbc_odbc || {};
        this.odbcEnabled = !!odbc.enabled;
        this.engines = odbc.engines || [];
      } catch {
        this.odbcEnabled = false;
        this.engines = [];
      }
      const jdbc = this.types.find((t) => t.key === 'jdbc');
      if (jdbc) jdbc.comingSoon = !this.odbcEnabled;
    },

    applyEnginePort() {
      const engine = this.engines.find((e) => e.key === this.odbc.engine);
      if (engine) this.odbc.port = engine.default_port;
    },

    _odbcFields() {
      return {
        connection_kind: 'jdbc_odbc',
        engine: this.odbc.engine,
        host: this.odbc.host,
        port: this.odbc.port,
        database: this.odbc.database,
        db_username: this.odbc.username,
        db_password: this.odbc.password,
      };
    },

    _payload() {
      if (this.activeType === 'jdbc') {
        return { name: this.odbc.name, ...this._odbcFields(), is_system: false };
      }
      return {
        name: this.sf.name,
        connection_kind: 'snowflake',
        account: this.sf.account,
        warehouse: this.sf.warehouse,
        database: this.sf.database,
        role: this.sf.role,
        snowflake_user: this.sf.username,
        private_key: this.sf.private_key,
        is_system: false,
      };
    },

    async handleTest() {
      this.testing = true;
      this.testResult = null;
      this.error = null;
      try {
        const body = this.activeType === 'jdbc' ? this._odbcFields() : {
          connection_kind: 'snowflake',
          account: this.sf.account,
          snowflake_user: this.sf.username,
          private_key: this.sf.private_key,
          warehouse: this.sf.warehouse,
          database: this.sf.database,
          role: this.sf.role,
        };
        const { data: t } = await axios.post(`${SOURCE_BASE}test-connection/`, body);
        this.testResult = { ok: !!t.success, message: t.message || (t.success ? 'Connection successful.' : 'Connection failed.') };
      } catch (e) {
        this.testResult = { ok: false, message: e.response?.data?.message || e.response?.data?.error || 'Test failed.' };
      } finally {
        this.testing = false;
      }
    },

    async handleSave() {
      this.saving = true;
      this.error = null;
      try {
        const { data } = await axios.post(SOURCE_BASE, this._payload());
        this.$emit('created', data);
        this.$emit('close');
      } catch (e) {
        const d = e.response?.data;
        this.error = d?.error || (d && typeof d === 'object' ? Object.values(d).flat()[0] : null) || 'Could not add the connection.';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.dc-label { display: block; font-size: 11px; font-weight: 500; color: #6b7280; margin-bottom: 4px; }
.dc-input {
  width: 100%; box-sizing: border-box; border: 1px solid #d1d5db; border-radius: 6px;
  padding: 7px 9px; font-size: 13px; color: #1f2937; background: #fff;
  transition: border-color .12s ease, box-shadow .12s ease;
}
.dc-input:focus { outline: none; border-color: var(--color-blue-dark, #4573D2); box-shadow: 0 0 0 2px rgba(69,115,210,.25); }

.dc-modal-enter-active, .dc-modal-leave-active { transition: opacity 0.18s ease; }
.dc-modal-enter-from, .dc-modal-leave-to { opacity: 0; }
.dc-modal-enter-active .dc-modal-panel { transition: transform 0.2s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.2s ease; }
.dc-modal-enter-from .dc-modal-panel { transform: translateY(8px) scale(0.97); opacity: 0; }
@media (prefers-reduced-motion: reduce) {
  .dc-modal-enter-active, .dc-modal-leave-active,
  .dc-modal-enter-active .dc-modal-panel { transition: none; }
}
</style>
